/**
 * Primary action control. Four hierarchies, two sizes, optional leading/trailing icon.
 * Spec: assets/handoffs/design_system_export/specs/Button.json
 *
 * @param {'primary'|'secondaryGray'|'tertiary'|'destructive'=primary} hierarchy
 * @param {'sm'|'md'=md} size
 * @param {ReactNode=} iconLeading
 * @param {ReactNode=} iconTrailing
 * @param {boolean=false} disabled
 * @param {boolean=false} loading - swaps the leading icon for a spinner, keeps the label, sets aria-busy
 * @param {boolean=false} fullWidth
 * @param {'button'|'submit'=button} type
 */
import React, { forwardRef } from "react";
import classNames from "classnames";
import styles from "./Button.module.scss";

const Button = forwardRef(
  (
    {
      hierarchy = "primary",
      size = "md",
      iconLeading = null,
      iconTrailing = null,
      disabled = false,
      loading = false,
      fullWidth = false,
      type = "button",
      className,
      children,
      ...rest
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        aria-busy={loading || undefined}
        className={classNames(
          styles.root,
          styles[`root--${size}`],
          styles[`root--${hierarchy}`],
          {
            [styles["is-full-width"]]: fullWidth,
          },
          className
        )}
        {...rest}
      >
        {loading ? (
          <span className={styles.spinner} aria-hidden="true" />
        ) : (
          iconLeading && (
            <span className={styles.icon} aria-hidden="true">
              {iconLeading}
            </span>
          )
        )}
        {children && <span className={styles.label}>{children}</span>}
        {!loading && iconTrailing && (
          <span className={styles.icon} aria-hidden="true">
            {iconTrailing}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
